// src/components/communication/EmailTemplatesList.js
import React, { useState, useEffect } from 'react';
import { 
  Search, Filter, Edit, Trash2, Copy, ChevronDown,
  PlusCircle, Layout, Eye, AlertCircle 
} from 'lucide-react';
import { Link, useNavigate } from 'react-router-dom';
import { 
  collection, query, where, orderBy, getDocs, deleteDoc, 
  doc, updateDoc, addDoc
} from 'firebase/firestore';
import { db } from '../../../firebase/config';
import { useAuth } from '../../../context/AuthContext';

const EmailTemplatesList = () => {
  const { currentUser, userProfile } = useAuth();
  const navigate = useNavigate();
  
  const [Layouts, setLayouts] = useState([]);
  const [isLoading, setIsLoading] = useState(true);
  const [error, setError] = useState(null);
  const [searchTerm, setSearchTerm] = useState('');
  const [filterStatus, setFilterStatus] = useState('all');
  
  // Fetch Layouts from Firestore
  useEffect(() => {
    const fetchLayouts = async () => {
      if (!currentUser || !userProfile?.businessId) {
        setIsLoading(false);
        return;
      }
      
      setIsLoading(true);
      setError(null);
      
      try {
        // Query Layouts for this business
        const LayoutsRef = collection(db, 'Layouts');
        const q = query(
          LayoutsRef,
          where('businessId', '==', userProfile.businessId),
          where('type', '==', 'email'),
          orderBy('updatedAt', 'desc')
        );
        
        const querySnapshot = await getDocs(q);
        const LayoutsList = [];
        
        querySnapshot.forEach((doc) => {
          const data = doc.data();
          LayoutsList.push({
            id: doc.id,
            ...data,
            createdAt: data.createdAt?.toDate?.() || null,
            updatedAt: data.updatedAt?.toDate?.() || null,
            lastUsed: data.lastUsed?.toDate?.() || null
          });
        });
        
        setLayouts(LayoutsList);
      } catch (err) {
        console.error("Error fetching Layouts:", err);
        setError("Failed to load Layouts. Please try again.");
      } finally {
        setIsLoading(false);
      }
    };
    
    fetchLayouts();
  }, [currentUser, userProfile]);
  
  // Filter Layouts based on search and filter
  const getFilteredLayouts = () => {
    return Layouts.filter(Layout => {
      const matchesSearch = 
        searchTerm === '' || 
        Layout.name.toLowerCase().includes(searchTerm.toLowerCase()) ||
        Layout.subject.toLowerCase().includes(searchTerm.toLowerCase());
        
      const matchesStatus = 
        filterStatus === 'all' || 
        Layout.status === filterStatus;
        
      return matchesSearch && matchesStatus;
    });
  };
  
  // Delete Layout
  const handleDeleteLayout = async (id) => {
    if (!window.confirm("Are you sure you want to delete this Layout? This action cannot be undone.")) {
      return;
    }
    
    try {
      await deleteDoc(doc(db, 'Layouts', id));
      
      // Update local state
      setLayouts(Layouts.filter(Layout => Layout.id !== id));
    } catch (err) {
      console.error("Error deleting Layout:", err);
      setError("Failed to delete Layout. Please try again.");
    }
  };
  
  // Duplicate Layout
  const handleDuplicateLayout = async (Layout) => {
    try {
      // Create a new Layout based on the existing one
      const newLayoutData = {
        ...Layout,
        name: `${Layout.name} (Copy)`,
        createdAt: new Date(),
        updatedAt: new Date(),
        createdBy: currentUser.uid,
        lastUsed: null
      };
      
      // Remove the id and timestamps that would be auto-generated
      delete newLayoutData.id;
      
      // Add the new Layout to Firestore
      const docRef = await addDoc(collection(db, 'Layouts'), newLayoutData);
      
      // Add the new Layout to the local state
      setLayouts([
        {
          ...newLayoutData,
          id: docRef.id
        },
        ...Layouts
      ]);
    } catch (err) {
      console.error("Error duplicating Layout:", err);
      setError("Failed to duplicate Layout. Please try again.");
    }
  };
  
  // Format date
  const formatDate = (date) => {
    if (!date) return '-';
    return new Date(date).toLocaleDateString('en-US', {
      year: 'numeric', month: 'short', day: 'numeric'
    });
  };
  
  return (
    <div className="space-y-6">
      <div className="flex justify-between items-center mb-4">
        <h3 className="text-lg font-medium text-gray-900">Email Layouts</h3>
        <Link 
          to="/communication/email/Layout/new"
          className="px-4 py-2 bg-[#190192] text-white rounded-lg flex items-center hover:bg-[#2C0DB5]"
        >
          <PlusCircle size={16} className="mr-2" />
          Create Layout
        </Link>
      </div>
      
      {/* Error display */}
      {error && (
        <div className="bg-red-50 border-l-4 border-red-500 p-4 text-red-700 mb-4">
          <div className="flex">
            <AlertCircle size={20} className="mr-2 flex-shrink-0" />
            <p>{error}</p>
          </div>
        </div>
      )}
      
      {/* Search and filter */}
      <div className="bg-white rounded-xl shadow-md overflow-hidden">
        <div className="flex items-center justify-between p-4 border-b">
          <div className="relative w-64">
            <Search className="absolute left-3 top-1/2 transform -translate-y-1/2 text-gray-400" size={18} />
            <input
              type="text"
              className="pl-10 pr-4 py-2 w-full rounded-lg text-sm border border-gray-200 focus:border-[#190192] focus:ring-2 focus:ring-[#190192] focus:ring-opacity-20"
              placeholder="Search Layouts..."
              value={searchTerm}
              onChange={(e) => setSearchTerm(e.target.value)}
            />
          </div>
          
          <div className="flex items-center space-x-3">
            <div className="relative">
              <select
                className="px-3 py-2 rounded-lg border border-gray-200 text-gray-600 appearance-none pr-8 focus:outline-none focus:ring-2 focus:ring-[#190192] focus:border-[#190192]"
                value={filterStatus}
                onChange={(e) => setFilterStatus(e.target.value)}
              >
                <option value="all">All Status</option>
                <option value="active">Active</option>
                <option value="draft">Draft</option>
                <option value="archived">Archived</option>
              </select>
              <ChevronDown size={16} className="absolute right-3 top-1/2 transform -translate-y-1/2 text-gray-400 pointer-events-none" />
            </div>
          </div>
        </div>
        
        {/* Layouts table */}
        {isLoading ? (
          <div className="p-8 text-center">
            <div className="animate-spin h-8 w-8 border-4 border-[#190192] rounded-full border-t-transparent mx-auto"></div>
            <p className="mt-2 text-gray-500">Loading Layouts...</p>
          </div>
        ) : getFilteredLayouts().length === 0 ? (
          <div className="p-8 text-center">
            <Layout size={48} className="text-gray-300 mx-auto mb-4" />
            {searchTerm || filterStatus !== 'all' ? (
              <p className="text-gray-500">No Layouts match your search criteria.</p>
            ) : (
              <>
                <p className="text-gray-700 font-medium">No email Layouts yet</p>
                <p className="text-gray-500 mt-1">Create your first Layout to get started.</p>
                <Link 
                  to="/communication/email/Layout/new"
                  className="mt-4 inline-flex items-center px-4 py-2 bg-[#190192] text-white rounded-lg hover:bg-[#2C0DB5]"
                >
                  <PlusCircle size={16} className="mr-2" />
                  Create Layout
                </Link>
              </>
            )}
          </div>
        ) : (
          <div className="overflow-x-auto">
            <table className="min-w-full divide-y divide-gray-200">
              <thead className="bg-gray-50">
                <tr>
                  <th scope="col" className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Layout Name</th>
                  <th scope="col" className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Subject</th>
                  <th scope="col" className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Last Used</th>
                  <th scope="col" className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Status</th>
                  <th scope="col" className="px-6 py-3 text-right text-xs font-medium text-gray-500 uppercase tracking-wider">Actions</th>
                </tr>
              </thead>
              <tbody className="bg-white divide-y divide-gray-200">
                {getFilteredLayouts().map((Layout) => (
                  <tr key={Layout.id} className="hover:bg-gray-50">
                    <td className="px-6 py-4 whitespace-nowrap">
                      <div className="flex items-center">
                        <Layout size={18} className="text-gray-400 mr-2" />
                        <span className="text-sm font-medium text-gray-900">{Layout.name}</span>
                      </div>
                    </td>
                    <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-500">{Layout.subject}</td>
                    <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-500">{formatDate(Layout.lastUsed)}</td>
                    <td className="px-6 py-4 whitespace-nowrap">
                      <span className={`px-2 inline-flex text-xs leading-5 font-semibold rounded-full ${
                        Layout.status === 'active' ? 'bg-green-100 text-green-800' :
                        Layout.status === 'draft' ? 'bg-yellow-100 text-yellow-800' :
                        'bg-gray-100 text-gray-800'
                      }`}>
                        {Layout.status.charAt(0).toUpperCase() + Layout.status.slice(1)}
                      </span>
                    </td>
                    <td className="px-6 py-4 whitespace-nowrap text-right text-sm font-medium">
                      <button 
                        className="text-gray-500 hover:text-gray-700 mr-3"
                        onClick={() => navigate(`/communication/email/preview/${Layout.id}`)}
                        title="Preview Layout"
                      >
                        <Eye size={16} />
                      </button>
                      <button 
                        className="text-[#190192] hover:text-[#2C0DB5] mr-3"
                        onClick={() => navigate(`/communication/email/Layout/edit/${Layout.id}`)}
                        title="Edit Layout"
                      >
                        <Edit size={16} />
                      </button>
                      <button 
                        className="text-gray-500 hover:text-blue-600 mr-3"
                        onClick={() => handleDuplicateLayout(Layout)}
                        title="Duplicate Layout"
                      >
                        <Copy size={16} />
                      </button>
                      <button 
                        className="text-gray-400 hover:text-red-600"
                        onClick={() => handleDeleteLayout(Layout.id)}
                        title="Delete Layout"
                      >
                        <Trash2 size={16} />
                      </button>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}
      </div>
    </div>
  );
};

export default EmailTemplatesList;